// PWA bootstrap + Web Push helpers (M17).

import type { ViewingPayload } from '@twinden/shared';

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
const installListeners = new Set<
  (event: BeforeInstallPromptEvent | null) => void
>();

export function getInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredInstallPrompt;
}

export function onInstallPromptChange(
  listener: (event: BeforeInstallPromptEvent | null) => void
): () => void {
  installListeners.add(listener);
  return () => installListeners.delete(listener);
}

export async function promptInstall(): Promise<
  'accepted' | 'dismissed' | null
> {
  if (!deferredInstallPrompt) return null;
  const event = deferredInstallPrompt;
  await event.prompt();
  const choice = await event.userChoice;
  deferredInstallPrompt = null;
  installListeners.forEach((listener) => listener(null));
  return choice.outcome;
}

let serviceWorkerFailed = false;

/**
 * Twinden's service worker failed to register in this browser (broken site
 * storage, #46): notifications can't work, and Settings says so (D308).
 */
export function serviceWorkerRegistrationFailed(): boolean {
  return serviceWorkerFailed;
}

export function setupPwa() {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstallPrompt = event as BeforeInstallPromptEvent;
    installListeners.forEach((listener) => listener(deferredInstallPrompt));
  });

  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    installListeners.forEach((listener) => listener(null));
  });

  if (navigator.serviceWorker) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch((err) => {
        // A failed SW must never break the app.
        serviceWorkerFailed = true;
        console.warn('Service worker registration failed', err);
      });
    });
  }
}

// VAPID public keys arrive base64url-encoded; pushManager wants an ArrayBuffer.
export function vapidPublicKeyToBytes(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const output = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return buffer;
}

// Badging API (M17 follow-up): unread count on the installed app icon. Not
// available everywhere, so it is a no-op when unsupported.
type BadgeNavigator = Navigator & {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

export function updateAppBadge(count: number): void {
  const nav = navigator as BadgeNavigator;
  if (
    typeof nav.setAppBadge !== 'function' ||
    typeof nav.clearAppBadge !== 'function'
  ) {
    return;
  }
  const update = count > 0 ? nav.setAppBadge(count) : nav.clearAppBadge();
  update.catch(() => undefined);
}

/** Twinden runs as an installed app (Home Screen or desktop install). */
export function isInstalledApp(): boolean {
  const iosStandalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return (
    iosStandalone ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  );
}

/** An iPhone or iPad (iPadOS reports itself as a Mac with a touch screen). */
export function isAppleMobile(
  userAgent = navigator.userAgent,
  maxTouchPoints = navigator.maxTouchPoints ?? 0
): boolean {
  return (
    /iPhone|iPad|iPod/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && maxTouchPoints > 1)
  );
}

/**
 * Twinden open in a browser tab on an iPhone or iPad. There, notifications
 * need the Home Screen app, which is a separate device with its own storage
 * (docs/new-devices.md, D139).
 */
export function isAppleMobileTab(): boolean {
  return isAppleMobile() && !isInstalledApp();
}

let pushEndpointHere: string | null = null;
const pushListeners = new Set<() => void>();

/**
 * Whether this device receives push notifications, refreshed on load and
 * whenever they're turned on or off. With them on, a notification makes the
 * sound, so the app doesn't play its own on top.
 */
export function isPushSubscribedHere(): boolean {
  return pushEndpointHere !== null;
}

/** Called whenever this device's push subscription changes. */
export function onPushSubscribedChange(listener: () => void): () => void {
  pushListeners.add(listener);
  return () => pushListeners.delete(listener);
}

export async function refreshPushSubscribed(): Promise<void> {
  let endpoint: string | null = null;
  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager?.getSubscription();
      endpoint = subscription?.endpoint ?? null;
    }
  } catch {
    endpoint = null;
  }
  if (endpoint === pushEndpointHere) return;
  pushEndpointHere = endpoint;
  pushListeners.forEach((listener) => listener());
}

/**
 * What the app tells the server about this device (WS_EVENTS.VIEWING): in
 * front or not, the chat on screen, and its own push subscription, so a call
 * push skips only this device while it rings in the app and still reaches
 * the person's other devices (issue #27).
 */
export function viewingPresence(conversationId: string | null): ViewingPayload {
  return {
    visible: document.visibilityState === 'visible',
    conversationId,
    pushEndpoint: pushEndpointHere,
  };
}
